import {
  Button,
  Card,
  InputNumber,
  message,
  Modal,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios, { type AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import {
  asyncErrorHandler,
  type ErrorResponse,
} from '@/utils/asyncErrorHandler';

import type {
  CleanKindAndReservationRequest,
  CleanReservationLimit,
} from '../../../sharedTypes';
import { cardBodyStyle } from './helper';
import styles from './index.module.scss';
import type {
  ChangedLimitList,
  EditReservationLimitDataType,
  ReservationLimitRequest,
  ReservationLimitResponse,
} from './type';

type DailyReservationCardProps = {
  date: number;
  result: (CleanReservationLimit & {
    cleanKindName: string | undefined;
  })[];
  showType: CleanKindAndReservationRequest['dataType'];
  getLatestLimitList: (
    key?: string,
    value?:
      | string
      | number
      | CleanKindAndReservationRequest['dataType']
      | string[]
  ) => void;
};

const { Text } = Typography;
const initErrorStatus = [
  {
    cleanKindId: '',
    isError: false,
  },
];

const DailyReservationCard = ({
  date,
  result,
  showType,
  getLatestLimitList,
}: DailyReservationCardProps) => {
  const [reservationLimitData, setReservationLimitData] =
    useState<ReservationLimitResponse>();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [changedLimits, setChangedLimits] = useState<ChangedLimitList[]>([]);
  const [errorStatus, setErrorStatus] =
    useState<{ cleanKindId: string; isError: boolean }[]>(initErrorStatus);

  const getDailyReservationLimit = async ({
    date,
    cleanKindIdList,
  }: ReservationLimitRequest) => {
    try {
      const res: AxiosResponse<ReservationLimitResponse> = await axios({
        method: 'GET',
        url: '/ezcon/api/fetchReservationLimit',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        params: {
          date,
          cleanKindIdList,
        },
      });
      setReservationLimitData(res.data);
      setChangedLimits(res.data.cleanReservationLimit);
    } catch (error) {
      const errorObj: ErrorResponse = error.response.data;
      asyncErrorHandler(
        errorObj,
        '無法獲取預約上限資料!',
        'getDailyReservationLimit-ERROR:'
      );
    }
  };

  const updateErrorStatus = (cleanKindId: string) => {
    const isExist = errorStatus.find(
      (error) => error.cleanKindId === cleanKindId
    );
    if (isExist) {
      const newErrorStatus = errorStatus.map((errObj) => {
        return errObj.cleanKindId === cleanKindId
          ? {
              cleanKindId,
              isError: true,
            }
          : errObj;
      });
      setErrorStatus(newErrorStatus);
    } else {
      const newErrorStatus = [...errorStatus, { cleanKindId, isError: true }];
      setErrorStatus(newErrorStatus);
    }
  };

  const closeModal = () => {
    setErrorStatus(initErrorStatus);
    setIsOpenModal(false);
  };

  const updateReservationLimits = async (changedLimits: ChangedLimitList[]) => {
    const isHasNullValue = changedLimits.some((limit) => {
      if (limit.limit === null) {
        updateErrorStatus(limit.cleanKindId);
      }
      return limit.limit === null;
    });

    if (isHasNullValue) {
      return;
    }

    const data = changedLimits.map((limit) => {
      return {
        ...limit,
        year: dayjs(date).get('year'),
        month: dayjs(date).get('month') + 1,
        day: dayjs(date).get('date'),
      };
    });

    try {
      await axios({
        method: 'PUT',
        url: '/ezcon/api/editReservationLimit',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        data,
      });
      getLatestLimitList();
      closeModal();
      message.success('預約上限更改成功');
    } catch (error) {
      const errorObj = error.response.data;
      asyncErrorHandler(
        errorObj,
        errorObj.displayMessage ?? '預約上限更改失敗',
        'updateReservationLimits-ERROR:'
      );
    }
  };

  const handleCardClick = async ({
    date,
    cleanKindIdList,
  }: ReservationLimitRequest) => {
    await getDailyReservationLimit({ date, cleanKindIdList });
    setIsOpenModal(true);
  };

  const columns: ColumnsType<EditReservationLimitDataType> = [
    {
      title: '清潔類別',
      dataIndex: 'cleanKindName',
    },
    {
      title: '預約上限',
      dataIndex: 'defaultLimit',
      render: (_value, record) => {
        const isError = errorStatus.find(
          (errObj) => errObj.cleanKindId === record.cleanKindId
        );
        return (
          <Space direction="vertical">
            <InputNumber
              min={record.reservedTimes}
              defaultValue={record.defaultLimit}
              status={isError !== undefined ? 'error' : ''}
              onChange={(value) => {
                if (value === null) {
                  updateErrorStatus(record.cleanKindId);
                }
                if (value) {
                  setErrorStatus(initErrorStatus);
                }
                setChangedLimits((prevState) => {
                  if (prevState !== undefined) {
                    const newLimitList = prevState.map((data) => {
                      return data.cleanKindId === record.cleanKindId
                        ? { ...data, limit: value }
                        : data;
                    });

                    return newLimitList;
                  }
                  return prevState;
                });
              }}
            />
            {isError ? <Text type="danger">不可為空值</Text> : <Text />}
          </Space>
        );
      },
    },
    {
      title: '預約次數',
      dataIndex: 'reservedTimes',
    },
    {
      title: '剩餘次數',
      dataIndex: 'numberOfRemaining',
    },
  ];

  const cleanKindIdList = result.map((data) => data.cleanKindId);
  const dataSource = reservationLimitData?.cleanReservationLimit.map(
    (limit) => {
      const { limit: defaultLimit, reservedTimes, ...restValue } = limit;
      return {
        ...restValue,
        defaultLimit,
        reservedTimes,
        numberOfRemaining: defaultLimit - reservedTimes,
      };
    }
  );

  return (
    <>
      <Card
        className={styles.grayBorderTop}
        hoverable
        bodyStyle={cardBodyStyle}
        onClick={() => handleCardClick({ date, cleanKindIdList })}
      >
        <div className={styles.date}>{dayjs(date).date()}</div>
        <Space direction="vertical" className={styles.kindsContainer}>
          {result.map((cleanKind, index) => {
            const className =
              index === 0
                ? styles.teal
                : index === 1
                ? styles.green
                : styles.gray;

            return (
              <div key={cleanKind.cleanKindId} className={className}>
                <span>{cleanKind.cleanKindName}</span>
                <span>
                  {showType === 'Limit'
                    ? `上限${cleanKind.limit}次`
                    : `剩餘${cleanKind.limit}次`}
                </span>
              </div>
            );
          })}
        </Space>
      </Card>
      <Modal
        centered
        destroyOnClose
        footer={null}
        open={isOpenModal}
        onCancel={closeModal}
      >
        <Text type="secondary">{`清潔日期:${dayjs(date).format(
          'YYYY/MM/DD'
        )}`}</Text>
        <Table
          bordered
          columns={columns}
          dataSource={dataSource}
          rowKey="cleanKindId"
          pagination={false}
        />
        <Text type="secondary" className={styles.unitDisplay}>
          （單位：次）
        </Text>
        <Space className={styles.footerButtonsContainer}>
          <Button onClick={closeModal}>取消</Button>
          <Button
            type="primary"
            onClick={() => updateReservationLimits(changedLimits)}
          >
            儲存
          </Button>
        </Space>
      </Modal>
    </>
  );
};

export default DailyReservationCard;
